<template>
  <div class="min-h-screen flex items-center justify-center bg-gray-50">
    <div class="max-w-md w-full p-8">
      <div class="text-center">
        <h1 class="text-6xl font-bold text-gray-900 mb-4">{{ error.statusCode || '500' }}</h1>
        <h2 class="text-2xl font-semibold text-gray-700 mb-2">
          {{ errorTitle }}
        </h2>
        <p class="text-gray-600 mb-8">
          {{ errorMessage }}
        </p>
        
        <div class="space-y-4">
          <button 
            @click="handleError" 
            class="pz-btn-filled w-full"
          >
            Go to Dashboard
          </button>
          
          <button 
            @click="clearError({ redirect: '/' })" 
            class="pz-btn-outlined w-full"
          >
            Go to Home
          </button>
        </div>
        
        <div v-if="isDevelopment" class="mt-8 p-4 bg-gray-100 rounded text-left">
          <h3 class="font-semibold mb-2">Error Details (Development Only):</h3>
          <pre class="text-xs overflow-auto">{{ error }}</pre>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted } from 'vue'
import { useNuxtApp, clearError } from '#imports'

const props = defineProps({
  error: Object
})

const { $sentry } = useNuxtApp()
const isDevelopment = process.env.NODE_ENV === 'development'

const errorTitle = computed(() => {
  if (props.error.statusCode === 404) {
    return 'Page Not Found'
  }
  if (props.error.statusCode === 403) {
    return 'Access Denied'
  }
  if (props.error.statusCode === 401) {
    return 'Authentication Required'
  }
  if (props.error.statusCode >= 500) {
    return 'Server Error'
  }
  return 'An Error Occurred'
})

const errorMessage = computed(() => {
  if (props.error.statusCode === 404) {
    return 'The page you are looking for does not exist.'
  }
  if (props.error.statusCode === 403) {
    return 'You do not have permission to access this resource.'
  }
  if (props.error.statusCode === 401) {
    return 'Please sign in to continue.'
  }
  if (props.error.statusCode >= 500) {
    return 'Something went wrong on our end. We\'ve been notified and are working on it.'
  }
  return props.error.statusMessage || 'An unexpected error occurred. Please try again.'
})

const handleError = () => {
  clearError({ redirect: '/dashboard' })
}

// Capture error to Sentry on mount
onMounted(() => {
  if ($sentry && props.error) {
    // Don't report 404 errors to Sentry
    if (props.error.statusCode !== 404) {
      $sentry.captureException(new Error(props.error.statusMessage || 'Application Error'), {
        tags: {
          error_page: true,
          status_code: props.error.statusCode,
        },
        extra: {
          error_data: props.error.data,
          error_stack: props.error.stack,
          url: props.error.url,
          statusCode: props.error.statusCode,
          statusMessage: props.error.statusMessage,
        },
        level: props.error.statusCode >= 500 ? 'error' : 'warning',
      })
    }
  }
})
</script>